import { ParcelContentEntry, DecodedItem, Asn, Asns } from 'stylewhere/api'
import { ShippingOperationConfig } from './RemoteOperation'

/**
 * Carica le letture degli altri parcels di un ASN usando Asns.asnContent
 * Il parcel corrente non è ancora confermato, quindi non sarà incluso nei dati
 * @param operationConfig Configurazione dell'operazione
 * @param asn ASN di cui caricare le letture
 * @param parcelId ID del parcel corrente (non utilizzato, mantenuto per compatibilità)
 * @returns Array di DecodedItem rappresentanti le letture degli altri parcels
 */
export const loadOtherParcelsReadings = async (
  operationConfig: ShippingOperationConfig,
  asn: Asn,
  parcelId?: string
): Promise<DecodedItem<string>[]> => {
  if (operationConfig.options.checklistMode !== 'shipment' || !asn.checklist) {
    return []
  }

  try {
    // Usa Asns.asnContent per ottenere i totali aggregati degli altri parcels
    // Il parcel corrente non è ancora confermato, quindi non sarà incluso nei dati
    const isItems = operationConfig.readingsDisplayMode === 'item'
    const groupedBy = isItems ? 'item' : 'product'

    const asnContentEntries: ParcelContentEntry[] = await Asns.asnContent(operationConfig.id, asn.id, groupedBy)

    if (!asnContentEntries || asnContentEntries.length === 0) {
      return []
    }

    // Converti ParcelContentEntry in DecodedItem
    // Nota: ParcelContentEntry ha sia entry.item che entry.product
    // Se entry.item.product non è popolato, usiamo entry.product
    const allOtherReadings: DecodedItem<string>[] = []

    asnContentEntries.forEach((entry) => {
      const item = entry.item
      const product = entry.product || item?.product

      if (!product || !product.code) {
        console.warn('[loadOtherParcelsReadings] Entry senza prodotto valido, skip:', entry)
        return
      }

      // Se item.product non è popolato ma entry.product sì, usiamo entry.product
      if (item && !item.product && entry.product) {
        item.product = entry.product as any // Product e TmrProduct sono compatibili a runtime
      }

      // Usa il campo 'detected' per creare il numero corretto di DecodedItem
      // Questo rappresenta le quantità già lette negli altri parcels
      const detected = entry.detected || 0

      // Crea un DecodedItem per ogni unità 'detected'
      // Se abbiamo un item completo, usalo, altrimenti crea un item minimale con il prodotto
      for (let i = 0; i < detected; i++) {
        // Se item è null ma product esiste, crea un item minimale per permettere il conteggio
        const itemToUse = item || ({ product } as any)
        const decodedItem: DecodedItem<string> = {
          item: itemToUse,
          detected: i === 0 ? detected : undefined, // Solo il primo item ha il campo detected completo
          expected: entry.expected,
          unexpected: entry.unexpected,
          identifierCode: item?.identifiers?.[0]?.code,
          statuses: []
        }
        allOtherReadings.push(decodedItem)
      }
    })

    return allOtherReadings
  } catch (error) {
    console.warn('Error loading other parcels readings:', error)
    // Non bloccare l'operazione se fallisce il caricamento delle letture degli altri parcels
    return []
  }
}
